import type {
  YearPlanResponse,
  TreatmentPlanDto,
  AssignedPatientResponse
} from '../api/generated';
import type { FlatInterventionGroup, GanttGroup, GanttSeries } from '../components/GanttChart/types';

const GROUP_BAR_WIDTH: Record<string, number> = {
  LARGE: 129,
  MEDIUM: 100,
  SMALL: 80
};
const GROUP_BLOCK_GAP = 4;
const CELL_PADDING = 12;
const DEFAULT_COL_WIDTH = 140;

export function flattenGroups(plan: TreatmentPlanDto): FlatInterventionGroup[] {
  return plan.weeks.flatMap((week) =>
    (week.groups ?? []).map((group) => ({
      ...group,
      weekNumber: week.weekNumber ?? 0
    }))
  );
}

export function aggregateAssignedPatients(plan: TreatmentPlanDto): AssignedPatientResponse[] {
  return plan.weeks.flatMap((week) =>
    (week.groups ?? []).flatMap((group) => group.assignedPatients ?? [])
  );
}

export function aggregateCapacity(plan: TreatmentPlanDto): number {
  return plan.weeks.reduce(
    (sum, week) =>
      sum + (week.groups ?? []).reduce((gs, g) => gs + (g.capacity ?? 0), 0),
    0
  );
}

export function buildGridTemplateColumns(weekWidths: number[]): string {
  return `230px ${weekWidths.map((w) => `${String(w)}px`).join(' ')}`;
}

function groupSeriesGroupsByWeek(series: GanttSeries): Map<number, FlatInterventionGroup[]> {
  const weekMap = new Map<number, FlatInterventionGroup[]>();
  for (const group of series.groups) {
    const weekNumber = group.weekNumber;
    const existing = weekMap.get(weekNumber);
    if (existing) existing.push(group);
    else weekMap.set(weekNumber, [group]);
  }
  return weekMap;
}

function computeSlotWidth(groupsInWeek: FlatInterventionGroup[]): number {
  const blocksWidth = groupsInWeek.reduce(
    (sum, group) => sum + (GROUP_BAR_WIDTH[group.barSize ?? 'LARGE'] ?? 100),
    0
  );
  return blocksWidth + (groupsInWeek.length - 1) * GROUP_BLOCK_GAP + CELL_PADDING;
}

function applySeriesWidths(series: GanttSeries, weekWidths: number[], totalWeeks: number): void {
  for (const [week, groupsInWeek] of groupSeriesGroupsByWeek(series)) {
    const idx = week - 1;
    if (idx < 0 || idx >= totalWeeks) continue;
    const slotWidth = computeSlotWidth(groupsInWeek);
    if (slotWidth > weekWidths[idx]) weekWidths[idx] = slotWidth;
  }
}

export function computePerWeekColumnWidths(groups: GanttGroup[], totalWeeks: number): number[] {
  const weekWidths = new Array(totalWeeks).fill(DEFAULT_COL_WIDTH) as number[];

  for (const ganttGroup of groups) {
    for (const series of ganttGroup.series) {
      applySeriesWidths(series, weekWidths, totalWeeks);
    }
  }

  return weekWidths;
}

export function buildGanttGroups(yearPlan: YearPlanResponse): GanttGroup[] {
  const plans = yearPlan.treatmentPlans;

  const grouped = new Map<string, typeof plans>();

  for (const plan of plans) {
    const templateId = plan.treatmentPlanTemplateId;
    const existing = grouped.get(templateId);
    if (existing) {
      existing.push(plan);
    } else {
      grouped.set(templateId, [plan]);
    }
  }

  const result: GanttGroup[] = [];

  for (const [templateId, templatePlans] of grouped) {
    result.push({
      templateId,
      templateName: templatePlans[0].name,
      series: templatePlans.map((plan, i) => ({
        planIndex: i,
        treatmentPlanId: plan.id,
        groups: flattenGroups(plan),
        seriesLabel: `M${String(i + 1)}`,
        strokeColor: plan.strokeColor,
        fillColor: plan.fillColor,
        highlightColor: plan.highlightColor
      }))
    });
  }

  return result;
}

export function packSeriesIntoRows(
  series: GanttSeries[]
): Map<number, number> {
  const assignment = new Map<number, number>();

  const ranges: { planIndex: number; min: number; max: number }[] = series.map((s) => {
    let min = Infinity;
    let max = -Infinity;
    for (const g of s.groups) {
      const w = g.weekNumber;
      if (w < min) min = w;
      if (w > max) max = w;
    }
    return { planIndex: s.planIndex, min, max };
  });

  const rows: number[] = [];

  for (const range of ranges) {
    if (range.min === Infinity) {
      assignment.set(range.planIndex, rows.length);
      rows.push(-Infinity);
      continue;
    }

    let placed = false;
    for (let r = 0; r < rows.length; r++) {
      if (rows[r] < range.min) {
        assignment.set(range.planIndex, r);
        rows[r] = range.max;
        placed = true;
        break;
      }
    }

    if (!placed) {
      assignment.set(range.planIndex, rows.length);
      rows.push(range.max);
    }
  }

  return assignment;
}
